import React from 'react';
import { useCurrentUser } from '../contexts/CurrentUserContext';

// Plan hierarchy — higher rank = more features. Mirrors the backend canonical
// ladder in lib/planAccess.js so the frontend gate and the API gates agree.
// Consumer tiers (free/personal/family) sit below pro: they unlock family/personal
// features elsewhere, not the business Pro features these gates guard.
const PLAN_RANK = {
  trial: 0, free: 0,
  basic: 1, basics: 1, personal: 1, family: 1, family_extra: 1,
  pro: 2, team: 3, enterprise: 4,
};
const REQUIRED_LABEL = { pro: 'Pro', team: 'Team Pro' };

/**
 * Determines whether the current user meets the required plan.
 * Uses the backend-computed `isTrialing` flag when available, falling back to
 * the subscriptionStatus field. A null/undefined plan is treated as 'basic'.
 */
export function userMeetsPlan(currentUser, requiredPlan) {
  if (!currentUser) return false;

  // Backend-computed trial flag (set by /api/auth/me)
  if (currentUser.isTrialing) return true;

  // Fallback: explicit Stripe trialing status
  if (currentUser.subscriptionStatus === 'trialing') return true;

  // Normalise plan: null → 'basic' (never treat missing plan as trial)
  const plan = (currentUser.plan || 'basic').toLowerCase();
  const userRank = PLAN_RANK[plan] ?? 1;
  const requiredRank = PLAN_RANK[(requiredPlan || 'pro').toLowerCase()] ?? 2;
  return userRank >= requiredRank;
}

/**
 * Wraps a feature and shows a locked upgrade prompt when the user's plan
 * doesn't meet the requirement.
 *
 * Props:
 *   requiredPlan   'pro' | 'team'
 *   featureName    Display name of the feature
 *   featureDescription  Custom one-liner shown under the name
 *   setFilter      Optional: navigate to Pricing page
 *   children       Content shown when access is granted
 */
export default function PlanGate({
  requiredPlan = 'pro',
  featureName,
  featureDescription,
  setFilter,
  onUpgrade,
  children,
}) {
  const { currentUser } = useCurrentUser();

  if (userMeetsPlan(currentUser, requiredPlan)) {
    return children;
  }

  const planLabel = REQUIRED_LABEL[requiredPlan] || requiredPlan;
  const name = featureName || 'This feature';
  // PREP-3b: a covered business member can't self-upgrade (billing is owner-only).
  // If they hit this gate the business plan itself is below the requirement, so
  // point them at the account owner instead of a self-checkout CTA.
  const isCoveredMember = !!currentUser?.isCoveredMember;
  const desc = isCoveredMember
    ? `${name} needs the ${planLabel} plan. Contact your account owner to upgrade your business plan.`
    : (featureDescription || `Upgrade to ${planLabel} to unlock ${name.toLowerCase()}.`);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4rem 2rem',
        textAlign: 'center',
        fontFamily: "'Raleway', sans-serif",
        minHeight: '320px',
      }}
    >
      <div
        style={{
          width: 60,
          height: 60,
          borderRadius: '50%',
          background: '#f1f2f4',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '1.25rem',
        }}
      >
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#0C2D48" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      </div>

      <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.2rem', fontWeight: 800, color: '#0C2D48' }}>
        {name}
      </h3>
      <p style={{ margin: '0 0 1.75rem', color: '#666', fontSize: '0.9rem', maxWidth: '380px', lineHeight: 1.6 }}>
        {desc}
      </p>

      {isCoveredMember ? (
        <div style={{ padding: '0.7rem 1.5rem', background: '#f1f2f4', color: '#556', borderRadius: '10px', fontFamily: "'Raleway', sans-serif", fontWeight: 600, fontSize: '0.85rem' }}>
          👤 Managed by your account owner
        </div>
      ) : (
        <button
          onClick={() => {
            if (onUpgrade) {
              onUpgrade(requiredPlan);
              return;
            }
            if (setFilter) setFilter('Settings');
          }}
          style={{
            padding: '0.7rem 2rem',
            background: '#0C2D48',
            color: '#fff',
            border: 'none',
            borderRadius: '10px',
            fontFamily: "'Raleway', sans-serif",
            fontWeight: 700,
            fontSize: '0.9rem',
            cursor: 'pointer',
            transition: 'opacity 0.15s',
          }}
          onMouseOver={e => (e.currentTarget.style.opacity = '0.82')}
          onMouseOut={e => (e.currentTarget.style.opacity = '1')}
        >
          Upgrade to {planLabel}
        </button>
      )}
    </div>
  );
}
